import { USER_TYPE_IDS } from "../components/Header/ProfileViewer/profile-viewer";

export interface User {
  _id: string;
  username: string;
  usernameUpdatedAt?: string;
  contentProviderAgreementAccepted?: boolean;
  userType: {
    _id: string;
    title: string;
  };
}

export interface UsernameChangeValidation {
  canChange: boolean;
  reason?: string;
  daysRemaining?: number;
  nextChangeDate?: Date;
}

/**
 * Validates if a user can change their username based on cooldown period
 * Creators: 60 days, Non-creators: 14 days
 */
export const validateUsernameChange = (
  user: User
): UsernameChangeValidation => {
  // Check if user is a creator
  const isCreator =
    user.userType._id === USER_TYPE_IDS.CREATOR ||
    user.userType.title === "model" ||
    user.contentProviderAgreementAccepted === true;

  // Set cooldown period based on user type
  const cooldownDays = isCreator ? 60 : 14;

  // If no previous username change, allow it
  if (!user.usernameUpdatedAt) {
    return {
      canChange: true,
    };
  }

  // Calculate days since last username change
  const lastChangeDate = new Date(user.usernameUpdatedAt);
  const currentDate = new Date();
  const daysSinceLastChange = Math.floor(
    (currentDate.getTime() - lastChangeDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Check if cooldown period has passed
  if (daysSinceLastChange >= cooldownDays) {
    return {
      canChange: true,
    };
  }

  // Calculate remaining days and next change date
  const daysRemaining = cooldownDays - daysSinceLastChange;
  const nextChangeDate = new Date(lastChangeDate);
  nextChangeDate.setDate(nextChangeDate.getDate() + cooldownDays);

  return {
    canChange: false,
    reason: `You can change your username again in ${daysRemaining} days`,
    daysRemaining,
    nextChangeDate,
  };
};

/**
 * Formats the next change date for display
 */
export const formatNextChangeDate = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Validates username format
 */
export const validateUsernameFormat = (
  username: string
): { isValid: boolean; error?: string } => {
  if (!username) {
    return { isValid: false, error: "Username is required" };
  }

  if (username.length < 3) {
    return {
      isValid: false,
      error: "Username must be at least 3 characters long",
    };
  }

  if (username.length > 20) {
    return {
      isValid: false,
      error: "Username must be less than 20 characters",
    };
  }

  // Only allow alphanumeric characters, underscores, and hyphens
  const usernameRegex = /^[a-zA-Z0-9_-]+$/;
  if (!usernameRegex.test(username)) {
    return {
      isValid: false,
      error:
        "Username can only contain letters, numbers, underscores, and hyphens",
    };
  }

  // Cannot start or end with underscore or hyphen
  if (
    username.startsWith("_") ||
    username.startsWith("-") ||
    username.endsWith("_") ||
    username.endsWith("-")
  ) {
    return {
      isValid: false,
      error: "Username cannot start or end with underscore or hyphen",
    };
  }

  return { isValid: true };
};
